import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import BeforeAfter from "@/components/BeforeAfter";
import { ChevronLeft, ChevronRight, MapPin, ArrowRight, Sparkles } from "lucide-react";

const ROTATE_MS = 8000;

export default function BeforeAfterReel() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [hovered, setHovered] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get("/projects?featured=true").then((r) => {
      const withPairs = r.data.filter((p) => p.before_image && p.after_image);
      setItems(withPairs.slice(0, 5));
    }).catch(() => setItems([]));
  }, []);

  useEffect(() => {
    clearInterval(timerRef.current);
    if (items.length < 2 || hovered) return;
    timerRef.current = setInterval(() => {
      setIdx((i) => (i + 1) % items.length);
    }, ROTATE_MS);
    return () => clearInterval(timerRef.current);
  }, [items.length, hovered]);

  if (items.length === 0) return null;

  const current = items[idx];
  const prev = () => setIdx((i) => (i - 1 + items.length) % items.length);
  const next = () => setIdx((i) => (i + 1) % items.length);

  return (
    <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24" data-testid="before-after-reel">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
        <div>
          <div className="label-overline mb-3 flex items-center gap-2">
            <Sparkles size={12} /> Transformation reel
          </div>
          <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight max-w-2xl">
            Drag to see the <span style={{color:'var(--accent)'}}>transformation.</span>
          </h2>
        </div>
        <div className="text-sm max-w-sm text-right hidden lg:block" style={{color:'var(--text-muted)'}}>
          Real Coastal Bend projects — before we showed up, and after we handed over the keys.
        </div>
      </div>

      <div
        className="relative"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div className="relative rounded-2xl overflow-hidden" style={{border: '1px solid var(--border-color)'}}>
          <BeforeAfter before={current.before_image} after={current.after_image} />

          {/* Project info overlay */}
          <div className="absolute left-5 bottom-5 right-5 lg:left-8 lg:bottom-8 lg:right-8 flex items-end justify-between gap-4 pointer-events-none">
            <div className="pointer-events-auto max-w-lg">
              <div className="badge mb-3" style={{backdropFilter:'blur(8px)'}}>{current.category.replace('_',' ')}</div>
              <h3 className="font-display text-2xl lg:text-3xl font-bold leading-tight" style={{textShadow:'0 2px 12px rgba(0,0,0,0.6)'}}>
                {current.title}
              </h3>
              <div className="mt-1.5 text-sm flex items-center gap-1.5" style={{color:'#E2E8F0', textShadow:'0 1px 6px rgba(0,0,0,0.6)'}}>
                <MapPin size={13} /> {current.location || 'Coastal Bend, TX'}
                {current.sqft && <span className="ml-3">· {current.sqft.toLocaleString()} sqft</span>}
              </div>
              <Link to={`/projects/${current.id}`} className="btn-primary !py-2 !px-4 !text-sm mt-4 pointer-events-auto" data-testid={`reel-cta-${current.id}`}>
                See this project <ArrowRight size={14}/>
              </Link>
            </div>
          </div>
        </div>

        {/* Carousel controls */}
        {items.length > 1 && (
          <>
            <button
              onClick={prev}
              aria-label="Previous"
              data-testid="reel-prev"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full flex items-center justify-center transition-colors hidden md:flex"
              style={{background:'rgba(11,19,43,0.75)', color:'#fff', border:'1px solid rgba(255,255,255,0.15)', backdropFilter:'blur(10px)'}}
            >
              <ChevronLeft size={20}/>
            </button>
            <button
              onClick={next}
              aria-label="Next"
              data-testid="reel-next"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full flex items-center justify-center transition-colors hidden md:flex"
              style={{background:'rgba(11,19,43,0.75)', color:'#fff', border:'1px solid rgba(255,255,255,0.15)', backdropFilter:'blur(10px)'}}
            >
              <ChevronRight size={20}/>
            </button>

            {/* Dots */}
            <div className="flex items-center justify-center gap-2 mt-6" data-testid="reel-dots">
              {items.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  data-testid={`reel-dot-${i}`}
                  className="h-1.5 rounded-full transition-all"
                  style={{
                    width: i === idx ? '32px' : '10px',
                    background: i === idx ? 'var(--accent)' : 'rgba(255,255,255,0.25)',
                  }}
                />
              ))}
              <span className="ml-4 text-xs tabular-nums" style={{color:'var(--text-muted)'}}>
                {String(idx + 1).padStart(2, '0')} / {String(items.length).padStart(2, '0')}
              </span>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
